import clsx from 'clsx';
import React, { ReactNode, useEffect, useState } from 'react';

interface ImagePopupProps {
  label?: string;
  icon?: ReactNode;
  imageUrl: string;
  altText?: string;
  windowTitle?: string;
}

export const ImagePopup: React.FC<ImagePopupProps> = ({
  label,
  icon,
  imageUrl,
  altText = 'Popup image',
  windowTitle = 'Secure Image View',
}) => {
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);

  // Function to open the popup window
  const openPopupWindow = () => {
    // Close any existing popup first
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close();
    }

    // Open a new window without any browser UI elements
    // Using specific parameters to ensure it opens as a separate window, not a tab
    const newWindow = window.open(
      '',
      'secureImageWindow', // Named window to ensure it's a new window, not tab
      'width=800,height=600,toolbar=no,menubar=no,location=no,status=no,titlebar=no,scrollbars=yes,resizable=yes,top=50,left=50,dependent=yes,dialog=yes'
    );

    if (newWindow) {
      // Set the title
      newWindow.document.title = windowTitle;

      // Create the HTML content with the image
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${windowTitle}</title>
          <style>
            body, html {
              margin: 0;
              padding: 0;
              height: 100%;
              width: 100%;
              overflow: auto;
              background-color: #000;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img {
              max-width: 100%;
              max-height: 100vh;
              object-fit: contain;
            }
            .close-button {
              position: absolute;
              top: 10px;
              right: 10px;
              background-color: rgba(0, 0, 0, 0.5);
              color: white;
              border: none;
              border-radius: 50%;
              width: 30px;
              height: 30px;
              cursor: pointer;
              font-size: 16px;
              display: flex;
              justify-content: center;
              align-items: center;
            }
          </style>
        </head>
        <body>
          <button class="close-button" onclick="window.close()">×</button>
          <img src="${imageUrl}" alt="${altText}" />
        </body>
        </html>
      `;

      // Write the content to the new window
      newWindow.document.write(htmlContent);
      newWindow.document.close();

      // Store the window reference in state
      setPopupWindow(newWindow);

      // Add event listener to detect when window is closed
      newWindow.addEventListener('beforeunload', () => {
        setPopupWindow(null);
      });
    }
  };

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close();
      }
    };
  }, [popupWindow]);

  return (
    <button
      className={clsx(
        'flex cursor-pointer items-center gap-1 text-sm hover:underline',
        'focus-visible:ring-primary-500 dark:ring-offset-dark-bg rounded-md p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 enabled:hover:bg-slate-200 dark:enabled:hover:bg-slate-700'
      )}
      onClick={openPopupWindow}
    >
      {label}
      {icon}
    </button>
  );
};

export default ImagePopup;
